'use client';

import { useEffect, useRef, useState } from 'react';
import { createOsanoBlockingController } from './osanoBlockingState';
import {
  OSANO_BLOCKED_CLASS,
  OSANO_CHOICE_REQUIRED_CLASS,
  OSANO_WINDOW_SELECTOR,
  type OsanoDomWindow,
  enterBlockingForPanel,
  getActivePanel,
  panelHasUsableControls,
} from './osanoDom';
import { OSANO_REGION_DATASET_KEY } from './osanoRegion';

// Fallback used only if Osano's own z-index can't be read (e.g. it hasn't
// set one and is relying on DOM order). Chosen to sit above normal page
// content without assuming a specific CMP vendor convention.
export const OSANO_OVERLAY_FALLBACK_Z_INDEX = 999999;

export { OSANO_REGION_DATASET_KEY };

// Existing exit-intent kill switches, already read by bx-forms'
// useTriggeredModal (GenericExitIntentModal, SemDesktopExitIntentModal),
// CustomScrollTriggerFaas, and PPLExitIntent. Reusing them here means every
// exit-intent surface gets suppressed while the EU blocking dialog is
// required, without any change to bx-forms or those components.
type ExitIntentWindow = Window & {
  faasBlockExitIntent?: boolean;
  allcaponeBlockExitIntent?: boolean;
};

const setExitIntentBlocked = (blocked: boolean) => {
  const extendedWindow = window as ExitIntentWindow;
  extendedWindow.faasBlockExitIntent = blocked;
  extendedWindow.allcaponeBlockExitIntent = blocked;
};

type BackdropState = {
  visible: boolean;
  // One below Osano's own computed z-index, so the backdrop always stacks
  // behind whatever value Osano happens to use for its banner, instead of
  // us guessing/hardcoding a number that could be wrong or change.
  zIndex: number;
};

const getOsanoWindowZIndex = (): number => {
  const el = document.querySelector<HTMLElement>(OSANO_WINDOW_SELECTOR);
  if (!el) return OSANO_OVERLAY_FALLBACK_Z_INDEX;

  const osanoZIndex = Number.parseInt(window.getComputedStyle(el).zIndex, 10);
  return Number.isNaN(osanoZIndex)
    ? OSANO_OVERLAY_FALLBACK_Z_INDEX
    : osanoZIndex - 1;
};

/**
 * Renders a dimmed, click-blocking backdrop only while a fresh EU visitor
 * has a required, usable choice on screen (handover section 1 and 5). Every
 * other region's Osano UI (US opt-out bar, "other" bar, US no-banner/widget)
 * is intentionally never backed by this backdrop — those layouts must not
 * block the page.
 *
 * Also drives the `us-none` behavior (hide the dialog, show the floating
 * widget, never save consent) and writes `data-osano-region` onto
 * `<html>` so critical CSS (loaded before `osano.js`) can lay the dialog
 * out correctly on first paint.
 *
 * While the blocking backdrop is up, also sets `window.faasBlockExitIntent`
 * and `window.allcaponeBlockExitIntent` to suppress exit-intent modals
 * (generic/SEM/profile FaaS forms and the PPL exit-intent widget) from
 * popping up over the required EU choice, clearing both back to `false` the
 * moment blocking is released.
 *
 * Fails open: if the SDK never initializes, the region can't be resolved,
 * or the dialog/drawer has no usable focusable control, no blocking
 * backdrop or focus trap is ever installed.
 */
const OsanoConsentOverlay = () => {
  const [backdrop, setBackdrop] = useState<BackdropState>({
    visible: false,
    zIndex: OSANO_OVERLAY_FALLBACK_Z_INDEX,
  });

  // Cleanup for the currently-active accessible-blocking lifecycle
  // (inert/aria/overflow/focus-trap), or null when nothing is blocking.
  const releaseBlockingRef = useRef<(() => void) | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const controller = createOsanoBlockingController();
    const root = document.documentElement;

    const releaseBlocking = () => {
      if (releaseBlockingRef.current) {
        releaseBlockingRef.current();
        releaseBlockingRef.current = null;
      }
      root.classList.remove(OSANO_BLOCKED_CLASS);
      setExitIntentBlocked(false);
      setBackdrop({ visible: false, zIndex: OSANO_OVERLAY_FALLBACK_Z_INDEX });
    };

    // Reconciles the DOM with the controller's current `required` flag.
    // Safe to call repeatedly (e.g. from the MutationObserver) — it only
    // acts on actual transitions.
    const sync = () => {
      const { required } = controller.getState();
      const isCurrentlyBlocking = releaseBlockingRef.current !== null;

      if (!required) {
        if (isCurrentlyBlocking) releaseBlocking();
        return;
      }

      const panel = getActivePanel();

      if (!panel || !panelHasUsableControls(panel)) {
        // Fail open: a required choice with no usable, visible panel must
        // never leave a blocking backdrop up.
        if (isCurrentlyBlocking) releaseBlocking();
        root.classList.remove(OSANO_BLOCKED_CLASS);
        return;
      }

      if (!isCurrentlyBlocking) {
        releaseBlockingRef.current = enterBlockingForPanel(panel, root);
      }

      root.classList.add(OSANO_BLOCKED_CLASS);
      setExitIntentBlocked(true);
      setBackdrop({ visible: true, zIndex: getOsanoWindowZIndex() });
    };

    const applyUsNoneBehavior = () => {
      const osano = (window as OsanoDomWindow).Osano;
      const cm = osano?.cm;
      if (!cm) return;

      // Never write consent here — only reads/UI calls. The dialog is
      // hidden and the widget shown; the visitor's choice (if any) is
      // untouched.
      cm.hideDialog?.();
      cm.showWidget?.();
    };

    const reopenDialogIfNeeded = () => {
      const { required } = controller.getState();
      if (!required) return;

      const osano = (window as OsanoDomWindow).Osano;
      const cm = osano?.cm;
      if (!cm) return;

      // Unsaved category toggles don't count as a choice: if the dialog
      // got hidden without a save and no drawer is open, reopen it.
      if (!cm.dialogOpen && !cm.drawerOpen) {
        cm.showDialog?.();
      }
    };

    controller.subscribe((state) => {
      root.dataset[OSANO_REGION_DATASET_KEY] = state.region ?? '';
      root.classList.toggle(OSANO_CHOICE_REQUIRED_CLASS, state.required);

      if (state.region === 'us-none' && !state.saved) {
        applyUsNoneBehavior();
      }

      sync();
    });

    const osano = (window as OsanoDomWindow).Osano;

    if (osano) {
      osano('onInitialized', (consent) => {
        const jurisdiction = osano.cm?.jurisdiction;
        controller.handleInitialized(jurisdiction, consent);
      });

      osano('onConsentSaved', () => {
        controller.handleConsentSaved();
      });

      osano('onUiChanged', (component) => {
        if (component !== 'dialog' && component !== 'drawer') return;
        // Osano's own properties (dialogOpen/drawerOpen) update after this
        // callback fires, so defer the read to let that settle first.
        queueMicrotask(() => {
          reopenDialogIfNeeded();
          sync();
        });
      });
    }

    const scheduleSync = () => {
      if (rafRef.current !== null) return;
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null;
        sync();
      });
    };

    const observer = new MutationObserver(scheduleSync);
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
    });

    // Also check once on mount, in case the SDK had already initialized
    // before this component subscribed (synchronous late-init callback) or
    // the dialog is already present in the DOM.
    sync();

    return () => {
      observer.disconnect();
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
      }
      releaseBlocking();
      root.classList.remove(OSANO_CHOICE_REQUIRED_CLASS);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per mount; all state is tracked via refs/closure.
  }, []);

  if (!backdrop.visible) return null;

  return (
    <div
      data-testid="osano-consent-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.45)',
        zIndex: backdrop.zIndex,
      }}
    />
  );
};

export default OsanoConsentOverlay;
